/* lib/table/columnWidths.js — ширина колонок таблиц Аналитики, чистая часть.

   МОДЕЛЬ. Хранятся ТОЛЬКО ширины столбцов, которые человек менял сам
   (перетаскиванием или с клавиатуры): `widths: {ключ: px}` в настройках таблицы
   (`useTableSettings`, тот же ключ localStorage, что порядок и видимость). Ширина
   живёт за ключом — переживает перестановку, скрытие и смену периода; двойной
   клик по ручке возвращает столбец в авто, «Столбцы → Сбросить» — все.

   Пока ни один видимый столбец не тронут, таблица живёт как жила: авто-раскладка
   браузера, ни одного лишнего элемента. С первым тронутым — `<colgroup>` и
   `table-layout: fixed` (так объединённые ячейки — группы, «Итого», двухэтажная
   шапка — получают ровно сумму своих столбцов). Ручной режим = авто-раскладка
   браузера с ЗАКРЕПЛЁННЫМИ тронутыми столбцами: тронутый держит ширину человека
   (многоточие в нём — его выбор), нетронутые — ровно те ширины, что браузер дал бы
   им в авто-раскладке этой таблицы на этих данных при таких тронутых (замер до
   отрисовки настоящим проходом авто-раскладки). Пересчёт — при фиксации правки
   ширины (отпускание, клавиша, возврат в авто), смене содержимого, набора
   столбцов, ширины контейнера и шрифтов; во время самого перетаскивания соседи
   стоят. Таблица, которой хватает места, остаётся в контейнере (и когда тронутый
   сужен уже своего содержимого: освобождённое место делят нетронутые); остаток
   уходит служебному столбцу без ширины, только когда отдать его некому (тронуты
   все видимые столбцы).

   Первая версия замораживала при первой правке ВСЕ столбцы: стоило данным
   вырасти (7 дней → 90), и нетронутые суммы уходили в многоточие.

   DOM и React — в screens/analytics/ColumnWidths.jsx. */

/** Уже 32px столбец не читается вовсе: 10 + 10 полей ячейки и 12 на значок
 *  или многоточие. Шире 1200 — уже не столбец, а промах мышью. */
export const MIN_COL_WIDTH = 32;
export const MAX_COL_WIDTH = 1200;
/** Шаг стрелки на ручке и крупный шаг с Shift. */
export const KEY_STEP = 16;
export const KEY_STEP_BIG = 64;

const isNum = (v) => typeof v === 'number' && Number.isFinite(v);

/* Единица раскладки браузера — 1/64 px (LayoutUnit Chromium; getBoundingClientRect
   отдаёт ширины в ней же). Ширины храним с этой точностью, а не целыми: снимок
   «как сейчас», округлённый до целых, срезал у столбцов «по содержимому» долю
   пикселя — и «12» в узком «#» превращалось в «1…» (живой кадр «Ликвидности»
   01.10). Кратные 1/64 числа точно представимы в JSON и в CSS-пикселях. */
const UNIT = 64;
const quantize = (px) => Math.round(px * UNIT) / UNIT;

/** px → ширина в [min, max] с точностью раскладки; не число — null («ширины
 *  нет», а не ноль). */
export function clampWidth(px, { min = MIN_COL_WIDTH, max = MAX_COL_WIDTH } = {}) {
  if (!isNum(px)) return null;
  const lo = Math.max(MIN_COL_WIDTH, isNum(min) ? min : MIN_COL_WIDTH);
  return Math.min(max, Math.max(lo, quantize(px)));
}

/** Минимум столбца: свой (`col.minWidth`), но не меньше общего. Нужен
 *  столбцам с действием в ячейке — у имени с кнопкой «Скрыть» под кнопку
 *  должно оставаться место при любой ширине. */
export function minWidthOf(col) {
  const own = col && isNum(col.minWidth) ? col.minWidth : 0;
  return Math.max(MIN_COL_WIDTH, own);
}

/** Хранимое значение → {ключ: px}. Значение пришло из localStorage, то есть
 *  могло быть записано старой версией или руками: берём только положительные
 *  числа и обрезаем края, остальное отбрасываем молча — кривая запись не
 *  должна ни ронять таблицу, ни схлопывать столбец в ноль. */
export function sanitizeWidths(raw) {
  const out = {};
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return out;
  for (const [key, v] of Object.entries(raw)) {
    if (!isNum(v) || v <= 0) continue;
    out[key] = clampWidth(v);
  }
  return out;
}

export function hasWidths(raw) {
  return Object.keys(sanitizeWidths(raw)).length > 0;
}

/** Новая карта с шириной одного столбца. Не число — карта без изменений. */
export function withWidth(widths, key, px, opts) {
  const base = sanitizeWidths(widths);
  const w = clampWidth(px, opts);
  if (w == null) return base;
  return { ...base, [key]: w };
}

export function withoutWidth(widths, key) {
  const next = sanitizeWidths(widths);
  delete next[key];
  return next;
}

/** Слияние: ширины из `add` поверх прежних; прежние без пары остаются
 *  (это ширины скрытых столбцов — вернётся столбец, вернётся и его ширина). */
export function mergeWidths(widths, add) {
  return { ...sanitizeWidths(widths), ...sanitizeWidths(add) };
}

/** План раскладки для набора столбцов `keys` (видимые, в порядке показа).
 *
 * manual    — хоть один видимый столбец тронут: нужна фиксированная раскладка.
 *             Ширины спрятанных столбцов на видимые не влияют.
 * touched   — ширины тронутых видимых столбцов (как их задал человек);
 * untouched — видимые без ширины: они ведут себя как в авто-раскладке. */
export function widthPlan(stored, keys) {
  const all = sanitizeWidths(stored);
  const touched = {};
  const untouched = [];
  for (const k of keys || []) {
    if (all[k] == null) untouched.push(k);
    else touched[k] = all[k];
  }
  return { manual: Object.keys(touched).length > 0, touched, untouched };
}

/* Ширины НЕТРОНУТЫХ столбцов в ручном режиме — ровно их ширины в авто-раскладке
   этой таблицы при закреплённых тронутых (замер — ColumnWidths.measureNatural,
   настоящий проход авто-раскладки). Своей раздачи свободного места здесь больше
   нет (была `distributeAuto`: max-content плюс доля места): авто-раскладка раздаёт
   его сама, а таблица шире контейнера стоит не на max-content, и раздача по нему
   делала ручной вид шире авто-вида (круг правок Ф3). */

/** Подпись замера: набор и порядок столбцов, их ширины в авто-раскладке и место.
 *  Совпала с прошлой — ширины нетронутых прежние (перерисовка без смены данных их
 *  не двигает). */
export function autoSignature(keys, natural, available) {
  const nat = (keys || []).map((k) => (natural && isNum(natural[k]) ? Math.round(natural[k] * UNIT) : -1));
  return (keys || []).join('\u0001') + '#' + nat.join(',') + '#' + Math.round((available || 0) * UNIT);
}

/** Замер «как сейчас» → ширины столбцов с точностью раскладки браузера.
 *
 * Снимок обязан воспроизвести таблицу ровно такой, какой её видит человек:
 * тогда первая правка меняет один столбец, а не все. Целые пиксели так не
 * умеют. Вниз до целого — тесные столбцы теряют долю пикселя, и содержимое
 * уходит в многоточие; к ближайшему — таблица выходит на пиксель шире
 * скроллпорта, и появляется прокрутка вбок; вниз с остатком «последнему» —
 * последний столбец ABC вырастал на 5px при перетаскивании совсем другого.
 * Незамеренный столбец (ширина 0 — нет ячейки или скрыт) выдуманной ширины не
 * получает.
 *
 * Минимума здесь НЕТ: снимок — это нетронутые столбцы «как есть», а минимум
 * (MIN_COL_WIDTH, свой у столбца, у заголовка) — правило для ширины, которую
 * задаёт человек. С ним первая правка поднимала узкий нетронутый «#» в
 * «Персонале» с 28,1 до 32px — сдвигался не тот столбец, что тянули. */
export function snapshotWidths(measured) {
  const out = {};
  for (const { key, width } of measured || []) {
    if (!isNum(width) || width <= 0) continue;
    out[key] = Math.min(MAX_COL_WIDTH, quantize(width));
  }
  return out;
}

/** Ширина после нажатия клавиши на ручке (role="separator"): стрелки — шаг,
 *  с Shift — крупный шаг, Home/End — края. Чужая клавиша — null. */
export function keyboardWidth(current, key, { shift = false, min, max } = {}) {
  const step = shift ? KEY_STEP_BIG : KEY_STEP;
  const lim = { min: isNum(min) ? min : MIN_COL_WIDTH, max: isNum(max) ? max : MAX_COL_WIDTH };
  if (key === 'ArrowRight') return clampWidth(current + step, lim);
  if (key === 'ArrowLeft') return clampWidth(current - step, lim);
  if (key === 'Home') return clampWidth(lim.min, lim);
  if (key === 'End') return clampWidth(lim.max, lim);
  return null;
}

/** Листья многоэтажной шапки: для каждого физического столбца — ячейка,
 *  которая накрывает его в ПОСЛЕДНЕМ ряду (сквозные «#», «Блюдо» с rowSpan=2 —
 *  это они сами). Тот же расчёт занятости клеток, что делает браузер, — и тот
 *  же, что у подписей карточного режима (ResponsiveTable.columnLabels).
 *
 * rows — массив рядов, ряд — массив ячеек `{colSpan, rowSpan, ...}`. Ячейка на
 * несколько столбцов отдаётся каждому из них: замер такой лист пропускает (по
 * нему не узнать, как ширина делится), и ширину берёт из тела таблицы. */
export function leafCells(rows) {
  const grid = [];
  (rows || []).forEach((cells, r) => {
    grid[r] = grid[r] || [];
    let c = 0;
    for (const cell of cells) {
      while (grid[r][c] !== undefined) c += 1;
      const cs = Math.max(1, Number(cell.colSpan) || 1);
      const rs = Math.max(1, Number(cell.rowSpan) || 1);
      for (let dr = 0; dr < rs; dr += 1) {
        grid[r + dr] = grid[r + dr] || [];
        for (let dc = 0; dc < cs; dc += 1) grid[r + dr][c + dc] = cell;
      }
      c += cs;
    }
  });
  if (!rows || !rows.length) return [];
  return grid[rows.length - 1] || [];
}

/** Ширины для `useTableSettings`: правки идут функциональным patch'ем от
 *  ПРЕЖНЕГО состояния. Первое перетаскивание записывает снимок всех столбцов и
 *  тут же — новую ширину своего; со снимком, взятым на рендере, вторая запись
 *  затёрла бы первую. */
export function widthsApi(raw, patch) {
  const widths = sanitizeWidths(raw);
  return {
    widths,
    hasWidths: Object.keys(widths).length > 0,
    setWidth(key, px, opts) { patch((prev) => ({ widths: withWidth(prev.widths, key, px, opts) })); },
    setWidths(map) { patch((prev) => ({ widths: mergeWidths(prev.widths, map) })); },
    clearWidth(key) { patch((prev) => ({ widths: withoutWidth(prev.widths, key) })); },
    resetWidths() { patch({ widths: null }); },
  };
}
